import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Table,
    Sheet
} from '@mui/joy';
import WorkIcon from '@mui/icons-material/Work';
import axiosConfig from '../util/axiosConfig';

const PlacementProfile = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [placementData, setPlacementData] = useState(null);

    useEffect(() => {
        fetchPlacementProfile();
    }, []);

    const fetchPlacementProfile = async () => {
        try {
            setError(null);
            const response = await axiosConfig.get('/students/placement-profile');
            setPlacementData(response.data);
        } catch (err) {
            console.error('Error fetching placement profile:', err);
            setError(err.response?.data?.errorMessage || 'Failed to load placement profile data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    gap: 2
                }}
            >
                <CircularProgress size="lg" />
                <Typography level="body-md">Loading placement profile data...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="danger" variant="soft">
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!placementData) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert color="warning" variant="soft">
                    No placement profile data available
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, maxWidth: '1400px', mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <WorkIcon sx={{ color: '#eb7a24', fontSize: '2rem' }} />
                <Typography level="h2" sx={{ color: '#eb7a24', fontWeight: 'bold' }}>
                    Placement Profile
                </Typography>
            </Box>
            <Sheet
                variant="outlined"
                sx={{
                    width: '100%',
                    borderRadius: 'sm',
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider'
                }}
            >
                <Table
                    aria-label="placement profile table"
                    sx={{
                        '& tbody tr:nth-child(even)': {
                            backgroundColor: '#f9f9f9'
                        },
                        '& tbody tr:hover': {
                            backgroundColor: '#fff5ed'
                        },
                        '& th': {
                            backgroundColor: '#eb7a24',
                            color: 'white',
                            fontWeight: 'bold',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1
                        },
                        '& td, & th': {
                            padding: '12px',
                            border: '1px solid',
                            borderColor: 'divider'
                        },
                        '& tbody td:first-of-type': {
                            width: '30%'
                        },
                        '& tbody td:nth-of-type(2)': {
                            width: '70%'
                        }
                    }}
                >
                    <tbody>
                        <tr>
                            <td>
                                <Typography level="body-md" sx={{ fontWeight: 'medium' }}>
                                    Placement status:
                                </Typography>
                            </td>
                            <td>
                                <Typography level="body-md">
                                    {placementData.placementStatus}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <Typography level="body-md" sx={{ fontWeight: 'medium' }}>
                                    W.Test/Interviews
                                </Typography>
                            </td>
                            <td>
                                <Typography level="body-md">
                                    {placementData.places || '-'}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <Typography level="body-md" sx={{ fontWeight: 'medium' }}>
                                    Relocation places
                                </Typography>
                            </td>
                            <td>
                                <Typography level="body-md">
                                    {placementData.rplaces || '-'}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <Typography level="body-md" sx={{ fontWeight: 'medium' }}>
                                    Expected Salary:
                                </Typography>
                            </td>
                            <td>
                                <Typography level="body-md">
                                    {placementData.expectedSalary || '-'}
                                </Typography>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2} style={{ textAlign: 'center' }}>
                                <Typography level="body-md">
                                    {placementData.updateType}: {placementData.time || 'N/A'}
                                </Typography>
                            </td>
                        </tr>
                    </tbody>
                </Table>
            </Sheet>
        </Box>
    );
};

export default PlacementProfile;

